import { tMessage } from "./runtime";
import { labelInLocale } from "./localizedLabels";
import { t, getLocale, translateMessage } from "./runtime";
import { AGENT_TRIGGER, STRATEGIC_MODE } from "./labels";

/**
 * Korean for the server-authored prose that is not a refusal.
 *
 * The core and the agent runner phrase these in English for the same reason
 * `errors.ts` exists: the strings double as server logs and as context the
 * nation agents read, so the Korean is put on at this edge instead.
 *
 * Everything here falls through unchanged when it does not recognise its input.
 * These are the strings a *deterministic* server writes; a live Codex nation
 * also writes free prose of its own (its goal, its summary, its doctrine lines,
 * the note on its own market order) and that is model output, not UI text — it
 * is not translated here and must not be, because rewriting what a nation said
 * would be putting words in its mouth.
 */

// ---------------------------------------------------------------------------
// how a round ended
// ---------------------------------------------------------------------------

/**
 * Both the result card and the session-ended banner and the world log print the
 * end reason, so the wording lives in exactly one place.
 */
export function formatEndReason(reason: string, locale = getLocale()): string {
  const trimmed = reason.trim();
  if (trimmed === "in progress")
    return translateMessage(locale, "serverText.in_progress", []);
  if (trimmed === "simulation time limit reached") {
    return translateMessage(
      locale,
      "serverText.seven_game_minutes_elapsed_score_finalized_using",
      [],
    );
  }
  const unification = trimmed.match(/^(.+) unified the continent$/);
  if (unification)
    return translateMessage(
      locale,
      "serverText.unified_the_continent_game_over",
      [
        {
          p0: unification[1],
        },
      ],
    );
  return trimmed;
}

export function formatEndTitle(reason: string): string {
  if (reason.trim() === "simulation time limit reached")
    return t("serverText.seven_minutes_elapsed_final_assets");
  if (/^(.+) unified the continent$/.test(reason.trim()))
    return t("serverText.unification_game_over");
  return t("serverText.game_ended_final_assets");
}

// ---------------------------------------------------------------------------
// why the session is not running what was asked for
// ---------------------------------------------------------------------------

/**
 * The server downgrades a session to mock for two quite different reasons, and
 * they look identical on screen: an amber 모의 에이전트 pill. The credential is
 * a setup problem the player has to go and fix; a full live-session pool clears
 * on its own in a few minutes. Telling them apart is the whole value of carrying
 * this string to the browser.
 */
export function translateAiModeNote(note: string): string {
  const trimmed = note.trim();

  const capacity = trimmed.match(
    /^"(.+)" is at capacity \((\d+) concurrent\); this session runs in mock$/,
  );
  if (capacity) {
    return t("serverText.all_paid_sessions_are_occupied_concurrent_this", {
      p0: capacity[2],
    });
  }

  const credential = trimmed.match(
    /^"(.+)" needs CODEX_API_KEY; falling back to mock$/,
  );
  if (credential) {
    return t("serverText.mode_needs_codex_api_key_using_mock", {
      p0: credential[1],
    });
  }

  return trimmed;
}

// ---------------------------------------------------------------------------
// agents
// ---------------------------------------------------------------------------

/** Why the agent runner gave up on a nation. Raw text stays as the tooltip. */
const AGENT_FAILURE_REASON: Record<string, string> = {
  get "session turn budget exhausted"() {
    return t("serverText.session_decision_budget_exhausted");
  },
};

export function translateAgentFailure(reason: string): string {
  if (Object.hasOwn(AGENT_FAILURE_REASON, reason.trim()))
    return AGENT_FAILURE_REASON[reason.trim()];
  console.warn("[cwb] Agent failure", reason);
  return t("serverText.agent_error");
}

/** `"STRATEGY_REVIEW on turn 6 moved 진 to EXPAND."` */
const DOCTRINE_REASON = /^([A-Z_]+) on turn (\d+) moved (\S+) to ([A-Z_]+)\.$/;

export function formatDoctrineReason(reason: string): string {
  const match = reason.trim().match(DOCTRINE_REASON);
  if (!match) return reason;
  // No 조사 after the trigger: it is a table value, and 으로/로 depends on
  // whether the word it follows ends in a consonant.
  return tMessage("serverText.turn", (_locale) => ({
    p0: match[3],
    p1: match[2],
    p2: labelInLocale(AGENT_TRIGGER, match[1], _locale) ?? match[1],
    p3: labelInLocale(STRATEGIC_MODE, match[4], _locale) ?? match[4],
  }));
}

// ---------------------------------------------------------------------------
// market
// ---------------------------------------------------------------------------

/** Order notes are untrusted world content, including familiar model phrases. */
export function translateOrderReason(reason: string): string {
  return reason;
}
